import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import HelpConnectApp from './helpconnect/HelpConnectApp';
import { ThemeProvider } from './contexts/ThemeContext';
import AppLayout from './components/AppLayout';
import PasswordResetModal from './components/PasswordResetModal';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ParticipantsPage from './pages/ParticipantsPage';
import ParticipantFormPage from './pages/ParticipantFormPage';
import ParticipantDetailPage from './pages/ParticipantDetailPage';
import CasesPage from './pages/CasesPage';
import CaseFormPage from './pages/CaseFormPage';
import CaseDetailPage from './pages/CaseDetailPage';
import CaseNotesPage from './pages/CaseNotesPage';
import CaseNoteFormPage from './pages/CaseNoteFormPage';
import ContractManagementPage from './pages/ContractManagementPage';
import ReportsPage from './pages/ReportsPage';
import CharlotteFoodPantryReportPage from './pages/CharlotteFoodPantryReportPage';
import NashvilleFoodPantryReportPage from './pages/NashvilleFoodPantryReportPage';
import AddictionsRecoveryReportPage from './pages/AddictionsRecoveryReportPage';
import ProgramTypesPage from './pages/ProgramTypesPage';
import ServiceTypesPage from './pages/ServiceTypesPage';
import ProgramServiceMappingsPage from './pages/ProgramServiceMappingsPage';
import EnrollmentTypesPage from './pages/EnrollmentTypesPage';
import FundingSourcesPage from './pages/FundingSourcesPage';
import OutboundNumbersPage from './pages/OutboundNumbersPage';
import HelpConnectResourcesPage from './pages/HelpConnectResourcesPage';
import HelpDeskTicketsReportPage from './pages/HelpDeskTicketsReportPage';
import SettingsPage from './pages/SettingsPage';
import SettingsDashboard from './pages/SettingsDashboard';
import FoodPantryQuickNoteTemplatesPage from './pages/FoodPantryQuickNoteTemplatesPage';
import SnapOutreachQuickNoteTemplatesPage from './pages/SnapOutreachQuickNoteTemplatesPage';
import QuickNoteGroupPage from './pages/QuickNoteGroupPage';
import SuccessCoachingProgramPage from './pages/SuccessCoachingProgramPage';
import UserManagementPage from './pages/UserManagementPage';
import RolesPermissionsPage from './pages/RolesPermissionsPage';
import TemplatesPage from './pages/TemplatesPage';
import TemplateAnalyticsPage from './pages/TemplateAnalyticsPage';
import JotformsPage from './pages/JotformsPage';
import AuditLogsPage from './pages/AuditLogsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import ManagementCalendarPage from './pages/ManagementCalendarPage';
import MessengerSettingsPage from './pages/MessengerSettingsPage';
import ProfilePage from './pages/ProfilePage';
import WebhookDiagnosticsPage from './pages/WebhookDiagnosticsPage';
import CourseManagementPage from './pages/CourseManagementPage';
import CaseSettingsPage from './pages/CaseSettingsPage';
import DropdownsPage from './pages/DropdownsPage';
import AISettingsPage from './pages/AISettingsPage';
import WorkflowsPage from './pages/WorkflowsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import ReportingSettingsPage from './pages/ReportingSettingsPage';
import DocumentSettingsPage from './pages/DocumentSettingsPage';
import SecuritySettingsPage from './pages/SecuritySettingsPage';
import DashboardBannersPage from './pages/DashboardBannersPage';
import AgencyHolidaysPage from './pages/AgencyHolidaysPage';
import EmailFooterPage from './pages/EmailFooterPage';
import PulsePage from './pages/PulsePage';
import PulseSettingsPage from './pages/PulseSettingsPage';
import RestoreMissingDocumentsPage from './pages/RestoreMissingDocumentsPage';
import MassDocumentUploadPage from './pages/MassDocumentUploadPage';
import FoodPantryCheckinKioskPage from './pages/FoodPantryCheckinKioskPage';
import FoodPantryCheckinKioskCharlottePage from './pages/FoodPantryCheckinKioskCharlottePage';
import FoodPantryQueuePage from './pages/FoodPantryQueuePage';
import ReferralTrackerPage from './pages/ReferralTrackerPage';
import FoodPantryAdminSettingsPage from './pages/FoodPantryAdminSettingsPage';
// SnapEtAutoAssignmentPage and CaseTransferPage are now integrated into SettingsDashboard SNAP E&T tab
import FoodPantryLobbyDisplayPage from './pages/FoodPantryLobbyDisplayPage';
import FoodPantryLobbyDisplayCharlottePage from './pages/FoodPantryLobbyDisplayCharlottePage';
import FoodPantryNowServingPage from './pages/FoodPantryNowServingPage';
import FoodPantryTabletQueuePage from './pages/FoodPantryTabletQueuePage';
import PDFSettingsPage from './pages/PDFSettingsPage';
import TaskTemplatesPage from './pages/TaskTemplatesPage';
import DocumentViewerPage from './pages/DocumentViewerPage';
import MassCaseNoteEntryPage from './pages/MassCaseNoteEntryPage';
import CommunicationsPage from './pages/CommunicationsPage';
import EmailDesignerTemplatesPage from './pages/EmailDesignerTemplatesPage';
import TokenRepairPage from './pages/TokenRepairPage';
import AppointmentSettingsPage from './pages/AppointmentSettingsPage';
import ParticipantCostConfigPage from './pages/ParticipantCostConfigPage';
import CaseloadReviewPage from './pages/CaseloadReviewPage';
import CaseloadFlagTicketPage from './pages/CaseloadFlagTicketPage';
import CaseloadFlagTicketsReportPage from './pages/CaseloadFlagTicketsReportPage';
import WixIntegrationPage from './pages/WixIntegrationPage';
import MergeFieldSettingsPage from './pages/MergeFieldSettingsPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1
    }
  }
});

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, mustResetPassword, clearPasswordReset, refreshUser } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const handlePasswordChanged = () => {
    clearPasswordReset();
    if (refreshUser) refreshUser();
  };

  return (
    <>
      <AppLayout>{children}</AppLayout>
      <PasswordResetModal 
        isOpen={mustResetPassword} 
        onPasswordChanged={handlePasswordChanged}
      />
    </>
  );
};

// Public Route wrapper (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/pulse" element={<PulsePage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/helpconnect/*" element={<HelpConnectApp />} />
      
      {/* Food Pantry Kiosk - Public (no auth required) */}
      <Route path="/checkin" element={<FoodPantryCheckinKioskPage />} />
      <Route path="/checkin-charlotte" element={<FoodPantryCheckinKioskCharlottePage />} />
      
      {/* Food Pantry Lobby Display - Public (no auth required) */}
      <Route path="/lobby-display" element={<FoodPantryLobbyDisplayPage />} />
      <Route path="/lobby-display-charlotte" element={<FoodPantryLobbyDisplayCharlottePage />} />

      {/* Food Pantry Staff Tablet Queue - Public (no auth required) */}
      <Route path="/queue" element={<FoodPantryTabletQueuePage />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      
      {/* Participants */}
      <Route path="/participants" element={<ProtectedRoute><ParticipantsPage /></ProtectedRoute>} />
      <Route path="/participants/new" element={<ProtectedRoute><ParticipantFormPage /></ProtectedRoute>} />
      <Route path="/participants/:id" element={<ProtectedRoute><ParticipantDetailPage /></ProtectedRoute>} />
      <Route path="/participants/:id/edit" element={<ProtectedRoute><ParticipantFormPage /></ProtectedRoute>} />
      
      {/* Cases */}
      <Route path="/cases" element={<ProtectedRoute><CasesPage /></ProtectedRoute>} />
      <Route path="/cases/new" element={<ProtectedRoute><CaseFormPage /></ProtectedRoute>} />
      <Route path="/cases/:id" element={<ProtectedRoute><CaseDetailPage /></ProtectedRoute>} />
      <Route path="/cases/:caseId/documents/:documentId/view" element={<ProtectedRoute><DocumentViewerPage /></ProtectedRoute>} />
      
      {/* Case Notes */}
      <Route path="/case-notes" element={<ProtectedRoute><CaseNotesPage /></ProtectedRoute>} />
      <Route path="/case-notes/new" element={<ProtectedRoute><CaseNoteFormPage /></ProtectedRoute>} />
      <Route path="/case-notes/:noteId/edit" element={<ProtectedRoute><CaseNoteFormPage /></ProtectedRoute>} />
      <Route path="/mass-case-notes" element={<ProtectedRoute><MassCaseNoteEntryPage /></ProtectedRoute>} />
      
      {/* Communications Module */}
      <Route path="/communications" element={<ProtectedRoute><CommunicationsPage /></ProtectedRoute>} />
      
      {/* Email Designer Templates */}
      <Route path="/settings/email-designer" element={<ProtectedRoute><EmailDesignerTemplatesPage /></ProtectedRoute>} />
      
      {/* Forms - Jotform Integration */}
      <Route path="/forms" element={<ProtectedRoute><JotformsPage /></ProtectedRoute>} />
      <Route path="/admin/jotforms" element={<ProtectedRoute><JotformsPage /></ProtectedRoute>} />
      
      {/* Documents - placeholder */}
      <Route path="/documents" element={<ProtectedRoute><PlaceholderPage title="Documents" description="Document management coming soon" /></ProtectedRoute>} />
      
      {/* Reports */}
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/reports/charlotte-food-pantry" element={<ProtectedRoute><CharlotteFoodPantryReportPage /></ProtectedRoute>} />
      <Route path="/reports/nashville-food-pantry" element={<ProtectedRoute><NashvilleFoodPantryReportPage /></ProtectedRoute>} />
      <Route path="/reports/addictions-recovery-program" element={<ProtectedRoute><AddictionsRecoveryReportPage /></ProtectedRoute>} />
      <Route path="/reports/snap-cert-expirations" element={<ProtectedRoute><ReportsPage reportType="snap-cert-expirations" /></ProtectedRoute>} />
      <Route path="/reports/snap-enrollment-expirations" element={<ProtectedRoute><ReportsPage reportType="snap-enrollment-expirations" /></ProtectedRoute>} />
      <Route path="/reports/snap-et-caseload" element={<ProtectedRoute><ReportsPage reportType="snap-et-caseload" /></ProtectedRoute>} />
      <Route path="/reports/snap-outreach" element={<ProtectedRoute><ReportsPage reportType="snap-outreach" /></ProtectedRoute>} />
      <Route path="/reports/pathways-modules" element={<ProtectedRoute><ReportsPage reportType="pathways-modules" /></ProtectedRoute>} />
      <Route path="/reports/help-institute-courses" element={<ProtectedRoute><ReportsPage reportType="help-institute-courses" /></ProtectedRoute>} />
      <Route path="/reports/participant-costs" element={<ProtectedRoute><ReportsPage reportType="participant-costs" /></ProtectedRoute>} />
      <Route path="/reports/employment-outcomes" element={<ProtectedRoute><ReportsPage reportType="employment-outcomes" /></ProtectedRoute>} />
      <Route path="/reports/retention" element={<ProtectedRoute><ReportsPage reportType="retention" /></ProtectedRoute>} />
      <Route path="/reports/enrollment-summary" element={<ProtectedRoute><ReportsPage reportType="enrollment-summary" /></ProtectedRoute>} />
      <Route path="/reports/master-enrollment-metrics" element={<ProtectedRoute><ReportsPage reportType="master-enrollment-metrics" /></ProtectedRoute>} />
      <Route path="/reports/caseload-review" element={<ProtectedRoute><ReportsPage reportType="caseload-review" /></ProtectedRoute>} />
      <Route path="/reports/snap-et-encounters" element={<ProtectedRoute><ReportsPage reportType="snap-et-encounters" /></ProtectedRoute>} />
      <Route path="/reports/community-service" element={<ProtectedRoute><ReportsPage reportType="community-service" /></ProtectedRoute>} />
      <Route path="/reports/helpdesk-tickets" element={<ProtectedRoute><HelpDeskTicketsReportPage /></ProtectedRoute>} />
      <Route path="/reports/caseload-review-tickets" element={<ProtectedRoute><CaseloadFlagTicketsReportPage /></ProtectedRoute>} />
      
      {/* Caseload Review */}
      <Route path="/caseload-review" element={<ProtectedRoute><CaseloadReviewPage /></ProtectedRoute>} />
      <Route path="/caseload-review/tickets/:flagId" element={<ProtectedRoute><CaseloadFlagTicketPage /></ProtectedRoute>} />
      
      {/* Admin */}
      <Route path="/admin/contracts" element={<ProtectedRoute><ContractManagementPage /></ProtectedRoute>} />
      <Route path="/admin/program-types" element={<ProtectedRoute><ProgramTypesPage /></ProtectedRoute>} />
      <Route path="/admin/service-types" element={<ProtectedRoute><ServiceTypesPage /></ProtectedRoute>} />
      <Route path="/admin/service-mappings" element={<ProtectedRoute><ProgramServiceMappingsPage /></ProtectedRoute>} />
      <Route path="/admin/enrollment-types" element={<ProtectedRoute><EnrollmentTypesPage /></ProtectedRoute>} />
      <Route path="/admin/funding-sources" element={<ProtectedRoute><FundingSourcesPage /></ProtectedRoute>} />
      <Route path="/admin/outbound-numbers" element={<ProtectedRoute><OutboundNumbersPage /></ProtectedRoute>} />
      <Route path="/admin/helpconnect-resources" element={<ProtectedRoute><HelpConnectResourcesPage /></ProtectedRoute>} />
      <Route path="/admin/participant-costs" element={<ProtectedRoute><ParticipantCostConfigPage /></ProtectedRoute>} />
      <Route path="/admin/audit-logs" element={<ProtectedRoute><AuditLogsPage /></ProtectedRoute>} />
      <Route path="/admin/restore-documents" element={<ProtectedRoute><RestoreMissingDocumentsPage /></ProtectedRoute>} />
      <Route path="/admin/mass-upload" element={<ProtectedRoute><MassDocumentUploadPage /></ProtectedRoute>} />
      <Route path="/admin/offices" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin/messenger-settings" element={<ProtectedRoute><MessengerSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/webhook-diagnostics" element={<ProtectedRoute><WebhookDiagnosticsPage /></ProtectedRoute>} />
      <Route path="/admin/courses" element={<ProtectedRoute><CourseManagementPage /></ProtectedRoute>} />
      <Route path="/admin/case-settings" element={<ProtectedRoute><CaseSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/dropdowns" element={<ProtectedRoute><DropdownsPage /></ProtectedRoute>} />
      <Route path="/admin/ai-settings" element={<ProtectedRoute><AISettingsPage /></ProtectedRoute>} />
      <Route path="/admin/workflows" element={<ProtectedRoute><WorkflowsPage /></ProtectedRoute>} />
      <Route path="/admin/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
      <Route path="/admin/integrations/wix" element={<ProtectedRoute><WixIntegrationPage /></ProtectedRoute>} />
      <Route path="/admin/reporting-settings" element={<ProtectedRoute><ReportingSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/documents" element={<ProtectedRoute><DocumentSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/pdf-settings" element={<ProtectedRoute><PDFSettingsPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><UserManagementPage /></ProtectedRoute>} />
      <Route path="/admin/roles" element={<ProtectedRoute><RolesPermissionsPage /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
      <Route path="/templates/analytics" element={<ProtectedRoute><TemplateAnalyticsPage /></ProtectedRoute>} />
      <Route path="/admin/task-templates" element={<ProtectedRoute><TaskTemplatesPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsDashboard /></ProtectedRoute>} />
      <Route path="/settings/food-pantry-quick-notes" element={<ProtectedRoute><FoodPantryQuickNoteTemplatesPage /></ProtectedRoute>} />
      <Route path="/settings/snap-outreach-quick-notes" element={<ProtectedRoute><SnapOutreachQuickNoteTemplatesPage /></ProtectedRoute>} />
      <Route path="/settings/quick-note-groups/:groupId" element={<ProtectedRoute><QuickNoteGroupPage /></ProtectedRoute>} />
      <Route path="/settings/success-coaching-program" element={<ProtectedRoute><SuccessCoachingProgramPage /></ProtectedRoute>} />
      <Route path="/settings/appointments" element={<ProtectedRoute><AppointmentSettingsPage /></ProtectedRoute>} />
      <Route path="/settings/merge-fields" element={<ProtectedRoute><MergeFieldSettingsPage /></ProtectedRoute>} />
      <Route path="/appointments" element={<ProtectedRoute><AppointmentsPage /></ProtectedRoute>} />
      <Route path="/appointments/management-calendar" element={<ProtectedRoute><ManagementCalendarPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/settings/security" element={<ProtectedRoute><SecuritySettingsPage /></ProtectedRoute>} />
      <Route path="/settings/dashboard-banners" element={<ProtectedRoute><DashboardBannersPage /></ProtectedRoute>} />
      <Route path="/settings/agency-holidays" element={<ProtectedRoute><AgencyHolidaysPage /></ProtectedRoute>} />
      <Route path="/settings/email-footer" element={<ProtectedRoute><EmailFooterPage /></ProtectedRoute>} />
      <Route path="/settings/pulse" element={<ProtectedRoute><PulseSettingsPage /></ProtectedRoute>} />
      
      {/* Food Pantry Check-In Management */}
      <Route path="/food-pantry/queue" element={<ProtectedRoute><FoodPantryQueuePage /></ProtectedRoute>} />
      <Route path="/referral-tracker" element={<ProtectedRoute><ReferralTrackerPage /></ProtectedRoute>} />
      <Route path="/food-pantry/now-serving" element={<ProtectedRoute><FoodPantryNowServingPage /></ProtectedRoute>} />
      <Route path="/admin/food-pantry-settings" element={<ProtectedRoute><FoodPantryAdminSettingsPage /></ProtectedRoute>} />
      {/* Redirect old auto-assignment and case-transfer pages to Settings SNAP E&T tab */}
      <Route path="/admin/snap-et-auto-assignment" element={<Navigate to="/settings?tab=snap-et" replace />} />
      <Route path="/admin/case-transfer" element={<Navigate to="/settings?tab=snap-et" replace />} />
      <Route path="/admin/token-repair" element={<ProtectedRoute><TokenRepairPage /></ProtectedRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// Placeholder component for pages not yet implemented
const PlaceholderPage = ({ title, description }) => (
  <div className="flex flex-col items-center justify-center h-64 text-center">
    <h1 className="text-2xl font-bold text-foreground mb-2">{title}</h1>
    <p className="text-muted-foreground">{description}</p>
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
